import { get, isNil } from 'lodash/fp';
import { CalculationMode, FinanceProductBasedOn, FinanceProductType, PaymentMode } from '../../../../api/types';
import useValidator from '../../../../utilities/useValidator';
import validators from '../../../../utilities/validators';
import { includesFinancing } from '../shared/groupings';
import { type FinanceProductFormValues } from './index';

const createRangeValidator = (settingsKey: string, withUnits: boolean = false) =>
    validators.compose(
        validators.requiredNumber(`${settingsKey}.min`),
        validators.requiredNumber(`${settingsKey}.max`),
        validators.requiredNumber(`${settingsKey}.step`),
        validators.requiredNumber(`${settingsKey}.default`),
        validators.only(
            values => {
                const min = get(`${settingsKey}.min`, values);
                const max = get(`${settingsKey}.max`, values);

                return Number.isFinite(min) && Number.isFinite(max);
            },
            validators.custom(`${settingsKey}.max`, (max, values, errors, context) => {
                const min = get(`${settingsKey}.min`, values);
                const maxUnit = get(`${settingsKey}.maxUnit`, values);
                const minUnit = get(`${settingsKey}.minUnit`, values);

                if (max < min && (!withUnits || maxUnit === minUnit)) {
                    return context.defaultMessages.maxLessThanMin;
                }

                return null;
            })
        ),
        validators.only(
            values => {
                const min = get(`${settingsKey}.min`, values);
                const max = get(`${settingsKey}.max`, values);

                return Number.isFinite(min) && Number.isFinite(max) && min <= max;
            },
            validators.custom(`${settingsKey}.default`, (defaultValue, values, errors, context) => {
                const min = get(`${settingsKey}.min`, values);
                const minUnit = get(`${settingsKey}.minUnit`, values);
                const max = get(`${settingsKey}.max`, values);
                const maxUnit = get(`${settingsKey}.maxUnit`, values);
                const unit = get(`${settingsKey}.unit`, values);

                if (
                    (defaultValue < min && (!withUnits || minUnit === unit)) ||
                    (defaultValue > max && (!withUnits || maxUnit === unit))
                ) {
                    return context.defaultMessages.defaultNotInRange;
                }

                return null;
            })
        )
    );

const validTerm = validators.compose(
    validators.requiredValue('term.isViewable'),
    validators.requiredValue('term.isEditable'),
    validators.requiredString('term.type'),
    createRangeValidator('term'),
    validators.custom('term.step', (step, values, errors, context) => {
        if (step <= 0) {
            return context.outerContext.t?.('common:formErrors.numberGreaterThan', { number: 0 });
        }

        return null;
    }),
    validators.only(
        values => get('type', values) === FinanceProductType.DeferredPrincipal,
        validators.compose(validators.requiredNumber('term.interestOnly'))
    )
);

const validDownPayment = validators.compose(
    validators.requiredValue('downPayment.isViewable'),
    validators.requiredValue('downPayment.isEditable'),
    validators.only(
        values => get('basedOn', values) === FinanceProductBasedOn.DownPayment,
        validators.compose(
            validators.requiredValue('downPayment.type'),
            validators.only(
                values => get('downPayment.type', values) === 'table',
                validators.compose(validators.requiredValue('downPayment.table'))
            ),
            validators.only(
                values => get('downPayment.type', values) === 'range',
                validators.compose(
                    createRangeValidator('downPayment', true),
                    validators.requiredValue('downPayment.unit'),
                    validators.requiredValue('downPayment.minUnit'),
                    validators.requiredValue('downPayment.maxUnit'),
                    validators.requiredValue('downPayment.stepUnit')
                )
            )
        )
    )
);

const validLoan = validators.compose(
    validators.requiredValue('loan.isViewable'),
    validators.requiredValue('loan.isEditable'),
    validators.only(
        values => get('basedOn', values) === FinanceProductBasedOn.Loan,
        validators.compose(
            validators.requiredValue('loan.type'),
            createRangeValidator('loan', true),
            validators.requiredValue('loan.unit'),
            validators.requiredValue('loan.minUnit'),
            validators.requiredValue('loan.maxUnit'),
            validators.requiredValue('loan.stepUnit')
        )
    )
);

const findDuplicatesTermAndBase = (array = []) => {
    const duplicates = [];
    const frequencyMap = {};

    for (let i = 0; i < array.length; i++) {
        const element = array[i];
        const key = `${element.term}_${element.base}`;

        if (frequencyMap[key]) {
            if (!duplicates.includes(element)) {
                duplicates.push(element);
            }
        } else {
            frequencyMap[key] = true;
        }
    }

    return duplicates;
};

export const validateInterestRateRandomTable = (values: FinanceProductFormValues, t) => {
    const { interestRate } = values;
    const { isGenerateStepsInTable, table } = interestRate || {};
    if (!isGenerateStepsInTable) {
        // empty input row validation
        const empties = [];
        table?.forEach((i, index) => {
            if (!(i.term && i.base && !isNil(i.value))) {
                empties.push(index + 1);
            }
        });
        if (empties.length) {
            if (empties.length === 1) {
                return t('financeProductDetails:messages.errors.interestRateTableEmptyInputRowSingle', {
                    indexes: empties,
                });
            }

            return t('financeProductDetails:messages.errors.interestRateTableEmptyInputRowMultiple', {
                indexes: empties,
            });
        }

        // duplicated row validation
        const duplicatesTermAndBase = findDuplicatesTermAndBase(table);
        const duplicates = [];
        if (duplicatesTermAndBase.length) {
            duplicatesTermAndBase.forEach(i => {
                const indexes = [];
                table?.forEach((t, index) => {
                    if (t.term === i.term && t.base === i.base) {
                        indexes.push(index + 1);
                    }
                });
                duplicates.push(indexes);
            });
        }
        if (duplicates.length) {
            if (duplicates.length === 1) {
                return t('financeProductDetails:messages.errors.interestRateTableDuplicatedRowSingle', {
                    indexes: duplicates[0],
                });
            }

            return t('financeProductDetails:messages.errors.interestRateTableDuplicatedRowMultiple', {
                indexes: duplicates.map(i => i.join(',')).join(' and '),
            });
        }
    }

    return null;
};

const validInterestRate = validators.compose(
    validators.requiredValue('interestRate.isViewable'),
    validators.requiredValue('interestRate.displayTotalInterestPayable'),
    validators.requiredValue('interestRate.type'),
    validators.only(
        values => get('interestRate.type', values) === 'table',
        validators.compose(
            validators.requiredValue('interestRate.table'),
            validators.requiredValue('interestRate.basedOn'),
            validators.requiredBoolean('interestRate.isGenerateStepsInTable'),
            validators.only(
                values => get('interestRate.isGenerateStepsInTable', values),
                validators.requiredNumber('interestRate.step')
            ),
            validators.requiredValue('interestRate.unit'),
            validators.requiredNumber('interestRate.min'),
            validators.requiredNumber('interestRate.max'),

            validators.only(
                values => {
                    const min = get('interestRate.min', values);
                    const max = get('interestRate.max', values);

                    return Number.isFinite(min) && Number.isFinite(max);
                },
                validators.custom('interestRate.max', (max, values, errors, context) => {
                    const min = get('interestRate.min', values);

                    if (max < min) {
                        return context.defaultMessages.maxLessThanMin;
                    }

                    return null;
                })
            )
        )
    ),
    validators.only(values => get('interestRate.type', values) === 'range', createRangeValidator('interestRate')),
    validators.only(
        values => get('interestRate.type', values) === 'fixed',
        validators.requiredNumber('interestRate.default')
    )
);

const validBalloon = validators.compose(
    validators.requiredValue('balloon.isViewable'),
    validators.requiredValue('balloon.basedOn'),
    validators.requiredValue('balloon.type'),
    validators.requiredValue('balloon.unit'),
    validators.only(
        values => get('balloon.type', values) === 'table',
        validators.compose(validators.requiredValue('balloon.table'))
    ),
    validators.only(
        values => get('balloon.type', values) === 'range',
        validators.compose(
            createRangeValidator('balloon', true),
            validators.requiredValue('balloon.minUnit'),
            validators.requiredValue('balloon.maxUnit'),
            validators.requiredValue('balloon.stepUnit')
        )
    )
);

// HP with Balloon GFV
const validBalloonGfv = validators.compose(
    validators.requiredValue('balloonGfv.isViewable'),
    validators.requiredValue('balloonGfv.isEditable'),
    validators.requiredNumber('balloonGfv.reducedMonthlyPayment'),
    validators.requiredString('balloonGfv.settlementInstalmentOn'),
    validators.requiredString('balloonGfv.residualValueUnit')
);

const validLease = validators.compose(validators.requiredValue('lease.type'), validators.requiredValue('lease.table'));

const validLeasePurchase = validators.compose(
    validators.requiredValue('residualValue.isViewable'),
    validators.requiredValue('residualValue.isEditable'),
    validators.requiredNumber('residualValue.averageMileage.min'),
    validators.requiredNumber('residualValue.averageMileage.max'),
    validators.requiredNumber('residualValue.averageMileage.step'),
    validators.requiredNumber('residualValue.averageMileage.default')
);

const validDeposit = validators.compose(
    validators.requiredValue('deposit.isViewable'),
    validators.requiredValue('deposit.isEditable'),
    validators.requiredValue('deposit.type'),
    validators.only(values => get('deposit.type', values) === 'table', validators.requiredValue('deposit.table')),
    validators.only(
        values => get('deposit.type', values) === 'range',
        validators.compose(createRangeValidator('deposit'), validators.requiredValue('deposit.unit'))
    )
);

// Validation for UCCL Leasing
const validLicensePlateFee = validators.compose(
    validators.requiredBoolean('licensePlateFee.isViewable'),
    validators.requiredBoolean('licensePlateFee.isEditable'),
    validators.requiredNumber('licensePlateFee.default'),
    validators.requiredNumber('licensePlateFee.min'),
    validators.requiredNumber('licensePlateFee.max'),
    validators.requiredNumber('licensePlateFee.step')
);

const validCommission = validators.compose(
    validators.requiredBoolean('commission.isViewable'),
    validators.requiredBoolean('commission.isEditable'),
    validators.requiredNumber('commission.default')
);

const validMonthlyPaymentFixedInterestRate = validators.compose(
    validators.requiredBoolean('monthlyPaymentFixedInterestRate.isViewable')
);

const validLicenseAndFuelTax = validators.compose(
    validators.requiredBoolean('licenseAndFuelTax.isViewable'),
    validators.requiredBoolean('licenseAndFuelTax.isEditable'),
    validators.forEach(
        'licenseAndFuelTax.fuelTable',
        validators.compose(
            validators.requiredNumber('min'),
            validators.requiredNumber('max'),
            validators.requiredNumber('value')
        )
    )
);

const validDisplacement = validators.compose(
    validators.requiredBoolean('displacement.isViewable'),
    validators.requiredBoolean('displacement.isEditable')
);

const validInsuranceFee = validators.compose(
    validators.requiredBoolean('insuranceFee.isViewable'),
    validators.requiredBoolean('insuranceFee.isEditable'),
    validators.forEach(
        'insuranceFee.table',
        validators.compose(validators.requiredNumber('value'), validators.requiredValue('variantSuiteId'))
    )
);

const validTaxLoss = validators.compose(
    validators.requiredBoolean('taxLoss.isViewable'),
    validators.requiredBoolean('taxLoss.isEditable')
);

const validUccl = validators.compose(
    validLicensePlateFee,
    validCommission,
    validMonthlyPaymentFixedInterestRate,
    validLicenseAndFuelTax,
    validDisplacement,
    validInsuranceFee,
    validTaxLoss
);

const validator = validators.compose(
    validators.requiredString('displayName'),
    validators.requiredString('legalName.defaultValue'),
    validators.requiredString('identifier'),
    validators.requiredString('bankId'),
    validators.requiresPeriod('period'),
    validators.requiredBoolean('isActive'),
    validators.requiredString('type'),
    validators.requiredString('deriveMethod'),
    validators.requiredNumber('order'),
    validators.only(
        values => {
            const from = get('vehicleReferenceParameters.finderVehicle.modelYear.from', values);
            const to = get('vehicleReferenceParameters.finderVehicle.modelYear.to', values);

            return Number.isFinite(from) && Number.isFinite(to);
        },
        validators.custom('vehicleReferenceParameters.finderVehicle.modelYear.to', (to, values, errors, context) => {
            const from = get('vehicleReferenceParameters.finderVehicle.modelYear.from', values);

            if (to < from) {
                return context.defaultMessages.maxLessThanMin;
            }

            return null;
        })
    ),
    validators.only(
        values => {
            const from = get('vehicleReferenceParameters.finderVehicle.mileage.from', values);
            const to = get('vehicleReferenceParameters.finderVehicle.mileage.to', values);

            return Number.isFinite(from) && Number.isFinite(to);
        },
        validators.custom('vehicleReferenceParameters.finderVehicle.mileage.to', (to, values, errors, context) => {
            const from = get('vehicleReferenceParameters.finderVehicle.mileage.from', values);

            if (to < from) {
                return context.defaultMessages.maxLessThanMin;
            }

            return null;
        })
    ),

    validators.only(
        values => includesFinancing(values.type),
        validators.compose(
            validators.requiredStringEnum('calculationMode', CalculationMode),
            validators.requiredStringEnum('payment.mode', PaymentMode),
            validators.requiredBoolean('payment.isViewable'),
            validators.requiredBoolean('payment.isEditable'),
            validators.requiredString('basedOn')
        )
    ),

    validators.only(
        values => {
            const type = get('type', values);

            return [
                FinanceProductType.HirePurchase,
                FinanceProductType.HirePurchaseWithBalloon,
                FinanceProductType.HirePurchaseWithBalloonGfv,
                FinanceProductType.DeferredPrincipal,
                FinanceProductType.UcclLeasing,
            ].includes(type);
        },
        validators.compose(validTerm, validDownPayment, validLoan, validInterestRate)
    ),

    validators.only(
        values => {
            const type = get('type', values);

            return type === FinanceProductType.Lease;
        },
        validators.compose(validTerm, validLease, validDeposit)
    ),

    /* Validators for UCCL only */
    validators.only(values => {
        const type = get('type', values);

        return type === FinanceProductType.UcclLeasing;
    }, validUccl),

    validators.only(values => {
        const type = get('type', values);

        return type === FinanceProductType.HirePurchaseWithBalloon;
    }, validBalloon),

    validators.only(values => {
        const type = get('type', values);

        return type === FinanceProductType.HirePurchaseWithBalloonGfv;
    }, validBalloonGfv),

    validators.only(values => {
        const type = get('type', values);

        return type === FinanceProductType.LeasePurchase;
    }, validLeasePurchase)
);

const useFinanceProductFormValidator = () => useValidator(validator);

export default useFinanceProductFormValidator;
